import {
    NewsActionsOptions,
    NewsFeedItem,
    NewsFeedUser,
    NewsInfo,
    NewsSearchType,
    NewsSelectionDictionary,
    NewsUserActions,
    NewsUserSettings,
    NewsViewSelectionOptions,
    UpdateNewsOptions,
} from '../libraries/mfc/MfcJsTypes'
import MfcNewsFeed from '../libraries/mfc/MfcNewsFeed'
import { pubSubNews } from './pubSub'
import { AppRouter } from './state.router'
import { THROTTLE_TIMERS } from './state.timers'

export interface NewsState {
    news: {
        information: NewsInfo
        newsItems: Array<NewsFeedItem>
        statusMessage?: string
        users?: {
            [propName: number]: NewsFeedUser
        }
        viewSelection: string
        searchQuery?: string
        searchType?: NewsSearchType
        isLoading: boolean
    }
    // timer: Timeout
}

export const NEWSSTATE: NewsState = {
    news: {
        information: {},
        newsItems: [],
        statusMessage: '',
        viewSelection: NewsSelectionDictionary.all_models,
        users: {},
        searchQuery: undefined,
        searchType: undefined,
        isLoading: false,
    },
}

export const updateNewsSelection = (selectionType: string) => {
    // remove search queries
    NEWSSTATE.news.searchType = undefined
    NEWSSTATE.news.searchQuery = undefined
    if (!window.MfcNewsFeedClient) return
    window.window.MfcNewsFeedClient.search_key = undefined
    window.MfcNewsFeedClient.search_string = undefined
    // update query
    const options: NewsViewSelectionOptions = {
        online_only: window.MfcNewsFeedClient.online_only,
        selection: selectionType,
    }
    window.MfcNewsFeedClient.changeViewSelection(options)
}

export const postNews = (message: string) => {
    if (!window.MfcNewsFeedClient) return
    const data = {
        message: message,
    }
    const options = {
        callback: (_args) => {
            window.MfcNewsFeedClient.changeViewSelection({
                online_only: window.MfcNewsFeedClient?.online_only,
                selection: 'my_news',
                callback: (nextStatusMessage) => {
                    NEWSSTATE.news.statusMessage = nextStatusMessage.message
                    getNews()
                },
            })
        },
    }
    window.MfcNewsFeedClient?.postNews(data, options)
}

export const deleteNewsItem = (postId: number) => {
    if (!window.MfcNewsFeedClient) return
    const options = {
        id: postId,
        callback: (_args) => {},
    }
    window.MfcNewsFeedClient.deleteNews(options)
}

export const toggleOnlineOnly = () => {
    if (!window.MfcNewsFeedClient) return
    const nextValue = !window.MfcNewsFeedClient.online_only
    const options: NewsViewSelectionOptions = {
        online_only: nextValue,
        selection: window.MfcNewsFeedClient.view_selection,
    }
    window.MfcNewsFeedClient.changeViewSelection({
        ...options,
    })
    getNews()
    return nextValue
}

export const updateSettings = (
    type: NewsUserSettings,
    enable: boolean | number,
) => {
    if (!window.MfcNewsFeedClient) return
    window.MfcNewsFeedClient.putOption({
        key: type,
        value: enable,
        callback: (_res) => {
            window.MfcNewsFeedClient.getInfo({
                callback: (_information) => {},
            })
        },
    })
}
export const userAction = (
    type: NewsUserActions,
    userId: number,
    removeFromList = false,
) => {
    if (!window.MfcNewsFeedClient) return

    const options: NewsActionsOptions = {
        key: type,
        value: userId,
    }

    window.MfcNewsFeedClient[removeFromList ? 'deleteOption' : 'putOption']({
        ...options,
        callback: (_args) => {
            window.MfcNewsFeedClient.getInfo({
                callback: (nextInformation) => {
                    const newsOptions = {
                        callback: () => {
                            NEWSSTATE.news.information = nextInformation
                            NEWSSTATE.news.newsItems =
                                window.MfcNewsFeedClient.news
                            NEWSSTATE.news.users =
                                window.MfcNewsFeedClient.users
                            NEWSSTATE.news.viewSelection =
                                window.MfcNewsFeedClient.view_selection
                            NEWSSTATE.news.searchQuery =
                                window.MfcNewsFeedClient.search_string
                            NEWSSTATE.news.searchType =
                                window.MfcNewsFeedClient.search_key
                            pubSubNews.emit('updateNews', NEWSSTATE.news)
                        },
                    }
                    window.MfcNewsFeedClient.getNews(newsOptions)
                },
            })
        },
    })
}

export const search = (type: NewsSearchType, query: string) => {
    if (!query || !type) return
    NEWSSTATE.news.searchType = type
    NEWSSTATE.news.searchQuery = query
    if (typeof window === 'undefined') return
    if (!window.MfcNewsFeedClient)
        window.MfcNewsFeedClient = new MfcNewsFeed(
            NEWSSTATE.news.searchQuery,
            NEWSSTATE.news.searchType,
        )
    else {
        window.MfcNewsFeedClient.search_key = type
        window.MfcNewsFeedClient.search_string = query
        window.MfcNewsFeedClient.changeViewSelection({})
        getNews()
    }
}

export const clearSearch = () => {
    if (!window.MfcNewsFeedClient) return
    window.MfcNewsFeedClient.search_key = undefined
    window.MfcNewsFeedClient.search_string = undefined
    NEWSSTATE.news.searchType = undefined
    NEWSSTATE.news.searchQuery = undefined
    // reset back to default view if search is cleared
    window.MfcNewsFeedClient.changeViewSelection({})
    getNews()
}

export const getNews = (options: UpdateNewsOptions = {}) => {
    if (typeof window === 'undefined' || NEWSSTATE.news.isLoading) return
    if (!window.MfcNewsFeedClient)
        window.MfcNewsFeedClient = new MfcNewsFeed(
            NEWSSTATE.news.searchQuery,
            NEWSSTATE.news.searchType,
        )
    NEWSSTATE.news.isLoading = true
    pubSubNews.emit('updateNews', NEWSSTATE.news)

    const newsOptions = {
        ...options,
        callback: () => {
            window.MfcNewsFeedClient.getInfo({
                callback: (nextInformation) => {
                    NEWSSTATE.news.information = nextInformation
                    NEWSSTATE.news.newsItems = window.MfcNewsFeedClient.news
                    NEWSSTATE.news.users = window.MfcNewsFeedClient.users
                    NEWSSTATE.news.viewSelection =
                        window.MfcNewsFeedClient.view_selection
                    NEWSSTATE.news.searchQuery =
                        window.MfcNewsFeedClient.search_string
                    NEWSSTATE.news.searchType =
                        window.MfcNewsFeedClient.search_key
                    NEWSSTATE.news.isLoading = false
                    pubSubNews.emit('updateNews', NEWSSTATE.news)
                },
            })
        },
    }
    window.MfcNewsFeedClient.getNews(newsOptions)
}

export const loadMore = () => {
    window.MfcNewsFeedClient.offset++
    getNews({ show_more: 1 })
}

if (typeof window !== 'undefined') {
    THROTTLE_TIMERS.news = setInterval(() => {
        if (AppRouter.route.visiblePage !== 'news') {
            return
        }
        getNews({
            update_only: 1,
        })
    }, 45000)
}
